import React, { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';
import { v4 as uuid } from 'uuid';
import uniqBy from 'lodash/uniqBy';
import FinalForm from '@/Framework/UI/Organisms/FinalForm';
import Modal from '@dealroadshow/uikit/core/components/Modal';
import ManagePortfoliosForm from './ManagePortfoliosForm';
import validate from './validate';
import { usePortfoliosContext } from '@/finsight/application/PortfolioMonitor/PortfoliosContext';
import { useManagePortfoliosContext } from '@/finsight/application/PortfolioMonitor/ManagePortfoliosContext';
import { IPortfolioItemPure } from '@/finsight/domain/vo/PortfolioMonitor/IPortfoliosCollection';

import styles from './managePortfoliosModal.scss';

interface IProps {
  isVisible: boolean,
  onCancel: () => void,
}

export interface IFieldData {
  portfolio: IPortfolioItemPure,
  id: string,
  value: string,
}

export interface IValuesMap {
  [key: string]: string,
}

export type TSetFieldsData = Dispatch<SetStateAction<IFieldData[]>>;

const ManagePortfoliosModal = ({
  isVisible,
  onCancel,
}: IProps) => {
  const { portfolioCollection } = usePortfoliosContext();
  const {
    managePortfolios,
    getPortfolios,
  } = useManagePortfoliosContext();

  const generateFieldsData = (portfolios: IPortfolioItemPure[]): IFieldData[] => portfolios.map(
    (portfolio) => ({
      portfolio,
      id: portfolio.id || uuid(),
      value: portfolio.name,
    }),
  );

  const mapFieldsDataToPortfolios = (fieldsData: IFieldData[]) => fieldsData.map(
    (fieldData, index) => ({
      name: fieldData.value,
      id: fieldData.portfolio.id,
      priority: index + 1,
    }),
  );

  const [fieldsData, setFieldsData] =
    useState<IFieldData[]>(generateFieldsData(portfolioCollection));

  useEffect(() => {
    setFieldsData((prevFields) => { // for keeping empty fields on delete one of them
      const newFields = prevFields.filter((item) => !item.portfolio.id);

      return uniqBy([...generateFieldsData(portfolioCollection), ...newFields], 'value');
    });
  }, [portfolioCollection, portfolioCollection.length]);

  const updateFieldValue = (fieldNameToFieldValue: IValuesMap, fieldData: IFieldData) => {
    const fieldName = fieldData.id;
    fieldNameToFieldValue[fieldName] = fieldData.value;
    return fieldNameToFieldValue;
  };

  const fieldValues = useMemo(() => fieldsData.reduce(updateFieldValue, {}), [fieldsData, portfolioCollection]);

  const onSubmit = useCallback(async () => {
    await managePortfolios(mapFieldsDataToPortfolios(fieldsData));

    await getPortfolios();
    onCancel();
  }, [fieldsData, portfolioCollection]);

  const renderForm = useCallback(({
    dirtySinceLastSubmit,
    hasSubmitErrors,
  }) => (
    <ManagePortfoliosForm
      onCancel={ onCancel }
      fieldsData={ fieldsData }
      setFieldsData={ setFieldsData }
      dirtySinceLastSubmit={ dirtySinceLastSubmit }
      hasSubmitErrors={ hasSubmitErrors }
    />
  ), [fieldsData]);

  const onValidate = useCallback((values: IValuesMap) => validate(values, fieldsData), [fieldsData]);

  return (
    <Modal
      className={ styles.modal }
      isVisible={ isVisible }
      onCloseClicked={ onCancel }
      hideOnOverlayClicked
      dataTest="managePortfoliosModal"
    >
      <FinalForm
        name="managePortfoliosForm"
        render={ renderForm }
        dataTest="managePortfoliosForm"
        initialValues={ fieldValues }
        onSubmit={ onSubmit }
        validate={ onValidate }
      />
    </Modal>
  );
};

export default ManagePortfoliosModal;
